/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */

/*
 * The fingerprint map.  Points arrive already folded onto their exact
 * coordinates by getFingerprintsGeo, each carrying n (observations stacked
 * there) and nf (distinct fingerprints).  MapLibre then clusters them further
 * by zoom level, summing n rather than counting features -- point_count would
 * report 166 for the IP layer when it really stands for 18743 observations.
 *
 * The two kinds of point live in separate sources so they cluster separately.
 * Sharing one source folded the business points of a metro area into the same
 * bubble as the datacenter IPs sitting on top of them, and since a datacenter
 * carries orders of magnitude more observations the merged bubble always read
 * as an IP one -- the dozens of companies behind it had no marker of their own
 * until the zoom pulled them back out.
 */

import { useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import BaseMap from './BaseMap';
import { plottableAnnos } from '../utils/geoCoords';
import { LABEL_FONT, drawableLabel } from '../utils/mapStyle';
import type { GeoBbox, GeoPoint } from '../data';

const IP_COLOR = '#F0F';
const BUSINESS_COLOR = '#FF0';

/*
 * A business point that stands for exactly one company arrives with that
 * company's name on it, and gets it drawn beside the dot the way the base map
 * draws city names: MapLibre hides a label that would collide with one already
 * placed, so density takes care of itself and only the zoom at which a name
 * first becomes eligible has to be decided here.
 *
 * Busier points earn their name earlier, which is the same idea as the city
 * scalerank gate but read off the observation count instead: base zoom for a
 * single observation, then one step earlier per decade of observations.  The
 * base is low because it is not what keeps the map clean at an overview zoom
 * -- clustering is, and a clustered point has no name to draw -- it only holds
 * names back from the handful of isolated points that survive out there.
 */
const NAME_BASE_ZOOM = 3;
const NAME_DECADE_ZOOM = 1.2;
const nameShown = [
    '>=', [ 'zoom' ],
    [ '-', NAME_BASE_ZOOM,
      [ '*', NAME_DECADE_ZOOM, [ 'log10', [ '+', [ 'get', 'n' ], 1 ] ] ] ]
];

// IP first, so the (usually far larger) datacenter bubbles paint underneath
// the business ones rather than burying them
const KINDS = [
    { type: 'ip', color: IP_COLOR },
    { type: 'business', color: BUSINESS_COLOR }
];

const clusterLayer = (type: string) => `clusters-${type}`;
const singleLayer = (type: string) => `single-${type}`;
const countLayer = (type: string) => `count-${type}`;
const COMPANY_LAYER = 'company-label';
const sourceId = (type: string) => `points-${type}`;
const kindOfSource = (source: string) => String(source || '').replace('points-', '');

/*
 * What a feature stands for: a cluster rolls its leaves up into obs, a lone
 * point already carries its own n.
 */
const COUNT = [ 'case', [ 'has', 'point_count' ], [ 'get', 'obs' ], [ 'get', 'n' ] ];

/*
 * 18743 in the width of a bubble is unreadable, so counts are abbreviated the
 * way they always were -- 18.7k, 1.2M -- only as an expression now that the
 * text is drawn by MapLibre rather than by a DOM node of our own.  A decimal
 * is dropped once the leading digits carry the magnitude on their own.
 */
const fixed = (digits: number) => ({ 'min-fraction-digits': digits, 'max-fraction-digits': digits });
const countText = [
    'case',
    [ '>=', COUNT, 10000000 ], [ 'concat', [ 'number-format', [ '/', COUNT, 1000000 ], fixed(0) ], 'M' ],
    [ '>=', COUNT, 1000000 ], [ 'concat', [ 'number-format', [ '/', COUNT, 1000000 ], fixed(1) ], 'M' ],
    [ '>=', COUNT, 10000 ], [ 'concat', [ 'number-format', [ '/', COUNT, 1000 ], fixed(0) ], 'k' ],
    [ '>=', COUNT, 1000 ], [ 'concat', [ 'number-format', [ '/', COUNT, 1000 ], fixed(1) ], 'k' ],
    [ 'number-format', COUNT, fixed(0) ]
];

export type ClusterPick = {
    bbox: GeoBbox;
    observations: number;
    nbusiness: number;
    nip: number;
    // Set when the pick resolved to one coordinate holding a single fingerprint
    fid?: number;
};

type GeoMapProps = {
    points: GeoPoint[];
    onPick?: (pick: ClusterPick) => void;
};

function toFeatureCollection(points: GeoPoint[], type: string): any {
    return {
        type: 'FeatureCollection',
        features: points.filter((p) => p.type === type).map((p) => {
            // Left off entirely when there is nothing drawable, so the label
            // layer's filter is just a has check
            const name = p.name ? drawableLabel(p.name) : '';
            return {
                type: 'Feature',
                properties: {
                    n: p.n,
                    nf: p.nf,
                    fid: p.fid,
                    type: p.type,
                    ...(name ? { name } : {})
                },
                geometry: { type: 'Point', coordinates: [ p.lon, p.lat ] }
            };
        })
    };
}

function bboxOf(coords: [ number, number ][]): GeoBbox {
    const lons = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    // A hair of padding so a cluster that resolves to a single coordinate
    // still describes a box the server can range scan
    const pad = 0.0001;
    return {
        minLon: Math.min(...lons) - pad,
        maxLon: Math.max(...lons) + pad,
        minLat: Math.min(...lats) - pad,
        maxLat: Math.max(...lats) + pad
    };
}

function GeoMap({ points, onPick }: GeoMapProps) {
    const [ map, setMap ] = useState<MapLibreMap | null>(null);
    const pickRef = useRef(onPick);
    pickRef.current = onPick;

    const plottable = plottableAnnos(points || []);

    // Sources, layers and interaction, installed once per map instance
    useEffect(() => {
        if (!map) {
            return;
        }
        const offs: (() => void)[] = [];

        for (const kind of KINDS) {
            const type = kind.type;
            map.addSource(sourceId(type), {
                type: 'geojson',
                data: toFeatureCollection([], type),
                cluster: true,
                // Loose enough that a point keeps its own dot, and its label,
                // as soon as the zoom gives it room: a wide radius held
                // neighbours together long after there was space to draw them
                // separately, and a cluster has no name to show
                clusterRadius: 28,
                clusterMaxZoom: 9,
                // Weighted rollup: every feature already stands for n observations
                clusterProperties: { obs: [ '+', [ 'get', 'n' ] ] }
            } as any);

            map.addLayer({
                id: clusterLayer(type),
                type: 'circle',
                source: sourceId(type),
                filter: [ 'has', 'point_count' ],
                paint: {
                    'circle-color': kind.color,
                    'circle-opacity': 0.55,
                    'circle-stroke-width': 1.5,
                    'circle-stroke-color': kind.color,
                    'circle-radius': [
                        'interpolate', [ 'linear' ], [ 'sqrt', [ 'get', 'obs' ] ],
                        1, 7,
                        10, 12,
                        50, 20,
                        150, 30
                    ]
                }
            });
            map.addLayer({
                id: singleLayer(type),
                type: 'circle',
                source: sourceId(type),
                filter: [ '!', [ 'has', 'point_count' ] ],
                paint: {
                    'circle-color': kind.color,
                    'circle-opacity': 0.85,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': 'rgba(0,0,0,0.6)',
                    'circle-radius': [
                        'interpolate', [ 'linear' ], [ 'sqrt', [ 'get', 'n' ] ],
                        1, 4,
                        10, 9,
                        50, 16
                    ]
                }
            });

        }

        /*
         * Company names, on the business points only and never on a cluster,
         * whose leaves may well be several different companies.
         *
         * Added after the circles on purpose: MapLibre places symbols from the
         * top layer down, so sitting above the base map is what lets a company
         * name win a collision against a city name rather than lose to it.
         * The city name is still the fallback, since a name that does not fit
         * simply is not drawn.
         */
        map.addLayer({
            id: COMPANY_LAYER,
            type: 'symbol',
            source: sourceId('business'),
            filter: [ 'all', [ '!', [ 'has', 'point_count' ] ], [ 'has', 'name' ],
                      nameShown ] as any,
            layout: {
                'text-field': [ 'get', 'name' ],
                'text-font': [ LABEL_FONT ],
                'text-size': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 4, 20, 10, 24 ],
                // Beside the dot rather than on it, leaving the middle to the
                // observation count marker
                'text-variable-anchor': [ 'left', 'right', 'top', 'bottom' ],
                'text-radial-offset': 0.9,
                'text-justify': 'auto',
                'text-padding': 2,
                // Long company names wrap instead of striping across the map
                'text-max-width': 12,
                // Collisions resolve in favour of the busier point, the way the
                // city labels resolve in favour of the more important city
                'symbol-sort-key': [ '-', 0, [ 'get', 'n' ] ]
            },
            paint: {
                'text-color': BUSINESS_COLOR,
                // Fixed rather than themed, matching the point colours it
                // labels; the halo keeps it legible over land and ocean alike
                'text-halo-color': 'rgba(0,0,0,0.85)',
                'text-halo-width': 1.2,
                'text-halo-blur': 0.5
            }
        } as any);

        /*
         * The observation counts, drawn last so they outrank every other label
         * on the map: a bubble without its number says nothing at all, while a
         * company name that loses its place comes back on the next zoom step.
         *
         * These used to be DOM markers positioned by hand, which meant they
         * took no part in collision and simply lay on top of whatever was
         * underneath them.  As a symbol layer they collide like everything
         * else, and the anchor list is what is left of the old stacking: a
         * count sits on its point when it can and steps above or below it when
         * a coincident business and IP bubble would otherwise hide one another.
         */
        for (const kind of KINDS) {
            map.addLayer({
                id: countLayer(kind.type),
                type: 'symbol',
                source: sourceId(kind.type),
                // A count of one is what a plain dot already says
                filter: [ '>=', COUNT, 2 ] as any,
                layout: {
                    'text-field': countText,
                    'text-font': [ LABEL_FONT ],
                    'text-size': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 2, 16, 8, 20 ],
                    'text-variable-anchor': [ 'center', 'top', 'bottom' ],
                    'text-radial-offset': 0.9,
                    'text-padding': 1,
                    // The bigger stack wins the spot
                    'symbol-sort-key': [ '-', 0, COUNT ]
                },
                paint: {
                    // Dark on the bubble it belongs to, with a light halo for
                    // the small dots where it overhangs the map instead
                    'text-color': '#000',
                    'text-halo-color': 'rgba(255,255,255,0.75)',
                    'text-halo-width': 1.5
                }
            } as any);
        }

        /*
         * One click path for every layer, labels included, so a company name
         * or a count opens exactly what its dot opens.  Which source the
         * feature came from says whether it is business or IP, and point_count
         * says whether it is a cluster to expand or a point to report.
         */
        const onFeatureClick = async (e: any) => {
            const f = e.features?.[0];
            if (!f) {
                return;
            }
            const type = kindOfSource(f.source);
            const props: any = f.properties;
            const coords = (f.geometry as any).coordinates as [ number, number ];
            if (!props.cluster) {
                const n = Number(props.n) || 0;
                // A folded point is still a stack: one coordinate here can hold
                // hundreds of fingerprints, so only a genuinely singular one
                // reports a fid for direct navigation
                pickRef.current?.({
                    bbox: bboxOf([ coords ]),
                    observations: n,
                    nbusiness: type === 'business' ? n : 0,
                    nip: type === 'ip' ? n : 0,
                    fid: Number(props.nf) === 1 ? Number(props.fid) : undefined
                });
                return;
            }
            const src = map.getSource(sourceId(type)) as GeoJSONSource;
            const obs = Number(props.obs) || 0;
            try {
                const leaves = await src.getClusterLeaves(props.cluster_id, 10000, 0);
                const at = (leaves || []).map(
                    (l: any) => l.geometry.coordinates as [ number, number ]);
                if (at.length) {
                    pickRef.current?.({
                        bbox: bboxOf(at),
                        observations: obs,
                        nbusiness: type === 'business' ? obs : 0,
                        nip: type === 'ip' ? obs : 0
                    });
                }
                const zoom = await src.getClusterExpansionZoom(props.cluster_id);
                map.easeTo({
                    center: coords,
                    zoom: Math.min(zoom, map.getMaxZoom()),
                    duration: 400
                });
            } catch (err) {
                console.error('cluster expand failed', err);
            }
        };

        const pointer = () => { map.getCanvas().style.cursor = 'pointer'; };
        const noPointer = () => { map.getCanvas().style.cursor = ''; };

        const clickable = [
            ...KINDS.flatMap((k) => [ clusterLayer(k.type), singleLayer(k.type),
                                      countLayer(k.type) ]),
            COMPANY_LAYER
        ];
        for (const id of clickable) {
            map.on('click', id, onFeatureClick);
            map.on('mouseenter', id, pointer);
            map.on('mouseleave', id, noPointer);
            offs.push(() => {
                map.off('click', id, onFeatureClick);
                map.off('mouseenter', id, pointer);
                map.off('mouseleave', id, noPointer);
            });
        }

        return () => {
            // The map may already be torn down by BaseMap at this point
            try {
                for (const off of offs) {
                    off();
                }
            } catch (e) {
                /* map already removed */
            }
        };
    }, [ map ]);

    // Data updates reuse the existing sources, no layer churn
    useEffect(() => {
        if (!map) {
            return;
        }
        for (const kind of KINDS) {
            const source = map.getSource(sourceId(kind.type)) as GeoJSONSource | undefined;
            if (source) {
                source.setData(toFeatureCollection(plottable, kind.type));
            }
        }
        // plottable is rebuilt every render, so the point array itself is the
        // meaningful dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ map, points ]);

    return <BaseMap onMapReady={setMap} center={[ 0, 20 ]} worldFit minZoom={0.5} maxZoom={14} />;
}

export default GeoMap;
